import { useState, useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Clock, Calendar, PlayCircle, MessageCircle, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { contactInfo } from '../../lib/data';

const VideoPage = () => {
  const { slug } = useParams();
  const [video, setVideo] = useState(null);
  const [relatedVideos, setRelatedVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const fetchVideo = async () => {
      try {
        const response = await fetch(`${API_URL}/api/videos/${slug}`);
        if (response.ok) {
          const data = await response.json();
          setVideo(data);
          
          // Fetch related videos
          const allVideosResponse = await fetch(`${API_URL}/api/videos`);
          if (allVideosResponse.ok) {
            const allVideos = await allVideosResponse.json();
            setRelatedVideos(allVideos.filter(v => v.slug !== slug && v.id !== slug).slice(0, 2));
          }
        } else {
          setNotFound(true);
        }
      } catch (error) {
        console.error('Failed to fetch video:', error);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    fetchVideo();
  }, [slug, API_URL]);

  const getYouTubeEmbedUrl = (url) => {
    const youtubeMatch = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
    if (youtubeMatch) {
      return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
    }
    return null;
  };

  const getVideoThumbnail = (url) => {
    const youtubeMatch = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
    if (youtubeMatch) {
      return `https://img.youtube.com/vi/${youtubeMatch[1]}/mqdefault.jpg`;
    }
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-sage-600 animate-spin" />
      </div>
    );
  }

  if (notFound) {
    return <Navigate to="/resources/videos" replace />;
  }

  const embedUrl = getYouTubeEmbedUrl(video.video_url);

  return (
    <div data-testid={`video-page-${slug}`}>
      {/* Hero Section */}
      <section className="bg-sage-900 py-8">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Link 
              to="/resources/videos" 
              className="inline-flex items-center gap-2 text-sage-300 hover:text-white mb-6 transition-colors group font-medium"
            >
              <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
              Back to Video Library
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Video Player Section */}
      <section className="bg-sage-900 pb-12">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="max-w-4xl mx-auto"
          >
            {embedUrl ? (
              <div className="aspect-video rounded-2xl overflow-hidden shadow-lg">
                <iframe
                  src={embedUrl}
                  title={video.title}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            ) : (
              <div className="bg-sage-800 aspect-video rounded-2xl flex items-center justify-center relative overflow-hidden shadow-lg">
                <div className="text-center">
                  <PlayCircle className="w-20 h-20 text-white/50 mx-auto mb-4" />
                  <p className="text-white/50 text-sm">Video Player</p>
                  <a 
                    href={video.video_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-sage-300 hover:text-white text-sm mt-2 inline-block"
                  >
                    Open video in new tab
                  </a>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-3 gap-16 max-w-6xl mx-auto">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <div className="flex items-center gap-4 mb-6">
                  <span className="text-xs font-semibold text-sage-600 uppercase tracking-wide">
                    {video.category}
                  </span>
                  {video.duration && (
                    <span className="flex items-center gap-1 text-xs text-charcoal/50">
                      <Clock className="w-3 h-3" />
                      {video.duration}
                    </span>
                  )}
                  <span className="flex items-center gap-1 text-xs text-charcoal/50">
                    <Calendar className="w-3 h-3" />
                    {new Date(video.created_at).toLocaleDateString('en-IE', { 
                      day: 'numeric', 
                      month: 'long', 
                      year: 'numeric' 
                    })}
                  </span>
                </div>

                <h1 className="font-serif text-3xl sm:text-4xl font-light text-charcoal leading-tight mb-8">
                  {video.title}
                </h1>

                <div className="text-lg text-charcoal/70 leading-relaxed whitespace-pre-wrap">
                  {video.description}
                </div>
              </motion.div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="sticky top-28 space-y-8"
              >
                {/* CTA Card */}
                <Card className="bg-sage-50 border-0 shadow-soft rounded-2xl">
                  <CardContent className="p-8 text-center">
                    <h3 className="font-serif text-xl font-medium text-charcoal mb-4">
                      Need Personalised Guidance?
                    </h3>
                    <p className="text-charcoal/60 text-sm mb-6 leading-relaxed">
                      Book an appointment for hands-on therapy tailored to your needs.
                    </p>
                    <Button
                      asChild
                      className="w-full bg-[#25D366] hover:bg-[#20BD5C] text-white rounded-full gap-2 py-6 font-semibold"
                      data-testid="video-whatsapp"
                    >
                      <a href={contactInfo.whatsapp} target="_blank" rel="noopener noreferrer">
                        <MessageCircle className="w-5 h-5" />
                        Message Ali
                      </a>
                    </Button>
                  </CardContent>
                </Card>

                {/* Related Videos */}
                {relatedVideos.length > 0 && (
                  <Card className="bg-white border-0 shadow-soft rounded-2xl">
                    <CardContent className="p-8">
                      <h3 className="font-serif text-xl font-medium text-charcoal mb-6">
                        Related Videos
                      </h3>
                      <div className="space-y-5">
                        {relatedVideos.map((relatedVideo) => (
                          <Link
                            key={relatedVideo.id}
                            to={`/resources/videos/${relatedVideo.slug || relatedVideo.id}`}
                            className="flex gap-4 group"
                          >
                            <div className="w-24 h-16 bg-sage-100 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                              {getVideoThumbnail(relatedVideo.video_url) ? (
                                <img 
                                  src={getVideoThumbnail(relatedVideo.video_url)}
                                  alt={relatedVideo.title}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <PlayCircle className="w-6 h-6 text-sage-400" />
                              )}
                            </div>
                            <div>
                              <h4 className="text-sm font-medium text-charcoal group-hover:text-sage-600 transition-colors line-clamp-2">
                                {relatedVideo.title}
                              </h4>
                              <p className="text-xs text-charcoal/50 mt-1">
                                {relatedVideo.duration || relatedVideo.category}
                              </p>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default VideoPage;
